package com.evoapps.evoshift.domain.schedule

import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.BusyElsewhere
import com.evoapps.evoshift.domain.model.ConflictType
import com.evoapps.evoshift.domain.model.DayStaffWarning
import com.evoapps.evoshift.domain.model.DayStaffing
import com.evoapps.evoshift.domain.model.Engagement
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftLeadRule
import com.evoapps.evoshift.domain.model.StaffCheck
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.model.TimeWindow
import com.evoapps.evoshift.domain.util.gapMinutesTo
import com.evoapps.evoshift.domain.util.overlaps
import kotlinx.datetime.LocalDate

/** SH-133: how the roster groups a person's issues (and which mark a shift gets). */
enum class IssueGroup {
    /** Two shifts at the same time, here or (forced) at another workplace. */
    DOUBLE_BOOKING,
    /** Too little rest: between shifts, in 24 hours, in a week (art. 132–133, or the company's rule). */
    REST,
    /** The labour code's hours: daily norm, 48 hours on average, overtime (employment contracts). */
    WORKING_HOURS,
    /** Planned on a day the person said they cannot work. */
    AVAILABILITY,
    /** Planned during an absence (vacation, sick leave). */
    ABSENCE,
    /** The medical exam or OHS training has run out by the shift's day. */
    STAFF_CHECKS,
    /** A day nobody leads, or a time only people who need supervision work. */
    SHIFT_LEAD
}

/**
 * SH-133: one thing the roster grid warns about, with the facts the UI words. [shiftIds] are the
 * shifts it marks (empty: the person's month as a whole); [userId] is null for a day's issue.
 */
sealed interface RosterIssue {
    val userId: String?
    val shiftIds: List<String>
    /** ISO day it is about (null: the month). */
    val date: String?
    val group: IssueGroup
    /** A conflict (red ⚠ on the shift) rather than a labour-code warning (amber gavel) or a day's note. */
    val isConflict: Boolean
    /** A legal requirement or the company's own rule (null: neither applies, e.g. availability). */
    val basis: LegalBasis?

    /** SH-112's labour-code checks ([LabourCodeCheck]); [contract] names the contract for a company rule. */
    data class Labour(val warning: LabourWarning, val contract: SystemContractType) : RosterIssue {
        override val userId: String get() = warning.userId
        override val shiftIds: List<String> get() = warning.shiftIds
        override val date: String? get() = when (warning) {
            is LabourWarning.ShortDailyRest -> warning.date
            is LabourWarning.ShortWeeklyRest -> warning.weekStart
            is LabourWarning.OverDailyNorm -> warning.date
            is LabourWarning.OverWeeklyAverage, is LabourWarning.OvertimeOverCap -> null
        }
        override val group: IssueGroup get() = when (warning) {
            is LabourWarning.ShortDailyRest, is LabourWarning.ShortWeeklyRest -> IssueGroup.REST
            else -> IssueGroup.WORKING_HOURS
        }
        override val isConflict: Boolean get() = false
        override val basis: LegalBasis get() = warning.basis
    }

    /** Two of the person's shifts in the roster overlap. */
    data class Overlap(
        override val userId: String,
        override val shiftIds: List<String>,
        override val date: String,
        /** The other shift's planned times (ISO), for "overlaps 14:00–22:00". */
        val otherStart: String,
        val otherEnd: String
    ) : RosterIssue {
        override val group: IssueGroup get() = IssueGroup.DOUBLE_BOOKING
        override val isConflict: Boolean get() = true
        override val basis: LegalBasis? get() = null
    }

    /**
     * Less than 11 hours between the end of one shift and the start of the next ([shiftIds]: both).
     * [sameDay]: two shifts on one day (a double shift) with [dayHours] of work. The labour code's
     * when the 24-hour rest (art. 132) is short too and the person is an employee; else the
     * company's rule ([contract]).
     */
    data class ShortBreak(
        override val userId: String,
        override val shiftIds: List<String>,
        override val date: String,
        val previousEnd: String,
        val nextStart: String,
        val sameDay: Boolean,
        val dayHours: Double,
        override val basis: LegalBasis,
        val contract: SystemContractType
    ) : RosterIssue {
        val breakHours: Double get() = ((isoMinutes(nextStart) ?: 0L) - (isoMinutes(previousEnd) ?: 0L)) / 60.0
        override val group: IssueGroup get() = IssueGroup.REST
        override val isConflict: Boolean get() = true
    }

    /**
     * A shift saved anyway over a conflict (soft validation, "assign anyway"): [type] as stored on
     * the shift; for [ConflictType.DOUBLE_BOOKED] the other shift is elsewhere ([elsewhere]).
     */
    data class Forced(
        override val userId: String,
        val shiftId: String,
        override val date: String,
        val type: ConflictType,
        val elsewhere: Boolean
    ) : RosterIssue {
        override val shiftIds: List<String> get() = listOf(shiftId)
        override val group: IssueGroup get() = when (type) {
            ConflictType.DOUBLE_BOOKED -> IssueGroup.DOUBLE_BOOKING
            ConflictType.ABSENCE_OVERLAP -> IssueGroup.ABSENCE
            ConflictType.REST_VIOLATION -> IssueGroup.REST
            ConflictType.UNAVAILABLE -> IssueGroup.AVAILABILITY
            ConflictType.EXPIRED_STAFF_CHECK -> IssueGroup.STAFF_CHECKS
        }
        override val isConflict: Boolean get() = true
        override val basis: LegalBasis? get() = null
    }

    /** Planned on a day the person marked as unavailable. */
    data class Unavailable(override val userId: String, val shiftId: String, override val date: String) : RosterIssue {
        override val shiftIds: List<String> get() = listOf(shiftId)
        override val group: IssueGroup get() = IssueGroup.AVAILABILITY
        override val isConflict: Boolean get() = true
        override val basis: LegalBasis? get() = null
    }

    /** Planned on a day their medical exam or OHS training ([checks]) no longer covers. */
    data class StaffChecksExpired(
        override val userId: String,
        val shiftId: String,
        override val date: String,
        val checks: Set<StaffCheck>
    ) : RosterIssue {
        override val shiftIds: List<String> get() = listOf(shiftId)
        override val group: IssueGroup get() = IssueGroup.STAFF_CHECKS
        override val isConflict: Boolean get() = true
        override val basis: LegalBasis? get() = null
    }

    /**
     * SH-135/136: the shift clashes with the person's work at another workplace ([BusyElsewhereCheck]):
     * it overlaps it ([ElsewhereIssue.OVERLAP], a double booking), or leaves less than 11 hours of
     * rest around it ([ElsewhereIssue.SHORT_REST]). The rest is the company's rule, not the labour
     * code's (each employer counts working time separately, the person rests only once).
     * [workplaceName]: the same owner's workplace, never another employer's.
     */
    data class Elsewhere(
        override val userId: String,
        val shiftId: String,
        override val date: String,
        val issue: ElsewhereIssue,
        val otherStart: String,
        val otherEnd: String,
        val workplaceName: String?
    ) : RosterIssue {
        override val shiftIds: List<String> get() = listOf(shiftId)
        override val group: IssueGroup get() = if (issue == ElsewhereIssue.OVERLAP) IssueGroup.DOUBLE_BOOKING else IssueGroup.REST
        /** The overlap is a conflict (the red ⚠); the short rest a warning, as the grid has marked it since SH-135. */
        override val isConflict: Boolean get() = issue == ElsewhereIssue.OVERLAP
        override val basis: LegalBasis? get() = if (issue == ElsewhereIssue.SHORT_REST) LegalBasis.COMPANY_POLICY else null
    }

    /** A day people work and nobody leads ([shiftIds]: the day's shifts). */
    data class NoShiftLead(override val date: String, override val shiftIds: List<String>) : RosterIssue {
        override val userId: String? get() = null
        override val group: IssueGroup get() = IssueGroup.SHIFT_LEAD
        override val isConflict: Boolean get() = false
        override val basis: LegalBasis? get() = null
    }

    /** At [windows] of the day only people who need supervision work ([shiftIds]: theirs). */
    data class OnlyNeedsSupervision(
        override val date: String,
        override val shiftIds: List<String>,
        val windows: List<TimeWindow>
    ) : RosterIssue {
        override val userId: String? get() = null
        override val group: IssueGroup get() = IssueGroup.SHIFT_LEAD
        override val isConflict: Boolean get() = false
        override val basis: LegalBasis? get() = null
    }
}

/** SH-133: every issue of a roster month, by shift and by person. */
data class RosterIssueReport(val issues: List<RosterIssue>) {
    val byShiftId: Map<String, List<RosterIssue>> by lazy {
        issues.flatMap { issue -> issue.shiftIds.distinct().map { it to issue } }.groupBy({ it.first }, { it.second })
    }

    /** The person's own issues (day issues show on the shifts and in the day headers). */
    val byUserId: Map<String, List<RosterIssue>> by lazy {
        issues.filter { it.userId != null }.groupBy { it.userId!! }
    }

    /** Shifts with a conflict: the red ⚠. */
    val conflictShiftIds: Set<String> by lazy { issues.filter { it.isConflict }.flatMapTo(HashSet()) { it.shiftIds } }

    /** Shifts with a labour-code warning: the amber gavel (when there is no conflict). */
    val labourShiftIds: Set<String> by lazy { issues.filterIsInstance<RosterIssue.Labour>().flatMapTo(HashSet()) { it.shiftIds } }

    companion object {
        val EMPTY = RosterIssueReport(emptyList())
    }
}

/**
 * SH-133: everything the roster grid warns about, in one place, so the grid explains each mark
 * and auto-assign can be checked against the very same list (the AutoScheduler agreement test):
 * - **double booking**: two of a person's shifts overlap, or one was saved over a shift elsewhere;
 * - **rest**: less than 11 hours between two shifts (a double shift when on one day), and the
 *   labour code's 24-hour and weekly rest ([LabourCodeCheck]); for employees by law, for other
 *   contracts only while the workplace applies the rest to everyone ([restForAllContracts]);
 * - **working hours** (employment contracts): daily norm, 48 hours on average, overtime;
 * - **availability**: planned on a day the person cannot work;
 * - **absence**: saved over an absence;
 * - **medical exam / OHS**: no longer valid on the shift's day;
 * - **work elsewhere** (SH-136): overlapping, or less than 11 hours of rest around, the person's
 *   shifts at their other workplaces ([BusyElsewhereCheck]);
 * - **shift lead**: a day nobody leads, or a time only people who need supervision work
 *   ([DayStaffing]).
 * A break that is short only between two shifts is shown once: the 24-hour rest warning of the
 * same shifts folds into it.
 */
object RosterIssues {

    fun find(
        shifts: List<Shift>,
        year: Int,
        month: Int,
        profiles: Map<String, LabourProfile> = emptyMap(),
        nominalMonthlyHours: Double = 160.0,
        restForAllContracts: Boolean = true,
        /** The people's engagements here (uid → engagement), for the day's lead and supervision. */
        engagements: Map<String, Engagement?> = emptyMap(),
        leadRule: ShiftLeadRule = ShiftLeadRule(),
        /** Each person's answers by ISO date. */
        availability: Map<String, Map<String, AvailabilityStatus>> = emptyMap(),
        /** The checks that have run out for a person by a day (StaffReadiness.expiredOn). */
        expiredChecks: (userId: String, date: LocalDate) -> Set<StaffCheck> = { _, _ -> emptySet() },
        /** SH-136: the people's shifts at their other workplaces (from the server, BusyElsewhereCheck). */
        busyElsewhere: List<BusyElsewhere> = emptyList()
    ): RosterIssueReport {
        val assigned = shifts.filter { it.userId != null }
        val labour = LabourCodeCheck.check(assigned, year, month, profiles, nominalMonthlyHours, restForAllContracts)
        fun profile(uid: String) = profiles[uid] ?: LabourProfile()
        val issues = ArrayList<RosterIssue>()

        // Overlaps and short breaks between consecutive shifts (DetectRosterConflictsUseCase's pairs).
        val breaks = ArrayList<RosterIssue.ShortBreak>()
        assigned.groupBy { it.userId!! }.entries.sortedBy { it.key }.forEach { (uid, theirs) ->
            val sorted = theirs.filter { isoMinutes(it.plannedTime.start) != null && isoMinutes(it.plannedTime.end) != null }
                .sortedBy { it.plannedTime.start }
            val p = profile(uid)
            sorted.zipWithNext().forEach { (a, b) ->
                when {
                    a.plannedTime.overlaps(b.plannedTime) -> issues += RosterIssue.Overlap(uid, listOf(a.id, b.id), b.date.take(10), a.plannedTime.start, a.plannedTime.end)
                    p.restRulesApply(restForAllContracts) && a.plannedTime.gapMinutesTo(b.plannedTime) < LabourCode.DAILY_REST_HOURS * 60L -> {
                        val sameDay = a.date.take(10) == b.date.take(10)
                        val dayHours = if (sameDay) sorted.filter { it.date.take(10) == a.date.take(10) }.sumOf { it.hours() } else 0.0
                        val rest = labour.warnings.filterIsInstance<LabourWarning.ShortDailyRest>()
                            .firstOrNull { it.userId == uid && a.id in it.shiftIds && b.id in it.shiftIds }
                        breaks += RosterIssue.ShortBreak(
                            uid, listOf(a.id, b.id), b.date.take(10), a.plannedTime.end, b.plannedTime.start, sameDay, dayHours,
                            basis = rest?.basis ?: LegalBasis.COMPANY_POLICY, contract = p.contract
                        )
                    }
                }
            }
        }
        issues += breaks

        // The labour code's warnings; a 24-hour rest made short only by one of the breaks above is that break.
        labour.warnings.forEach { w ->
            val folded = w is LabourWarning.ShortDailyRest && w.previousEnd != null &&
                breaks.any { it.userId == w.userId && it.shiftIds.all { id -> id in w.shiftIds } && it.previousEnd == w.previousEnd }
            if (!folded) issues += RosterIssue.Labour(w, profile(w.userId).contract)
        }

        // SH-136: work at another workplace (overlaps, and the company's 11 hours of rest around it).
        BusyElsewhereCheck.clashes(assigned, busyElsewhere).forEach { clash ->
            issues += RosterIssue.Elsewhere(
                clash.shift.userId!!, clash.shift.id, clash.shift.date.take(10), clash.issue,
                clash.other.plannedTime.start, clash.other.plannedTime.end, clash.other.workplaceName
            )
        }

        // Saved anyway over a conflict; what the roster shows on its own is not repeated.
        val ids = shifts.mapTo(HashSet()) { it.id }
        assigned.forEach { shift ->
            val uid = shift.userId!!
            val date = shift.date.take(10)
            val status = availability[uid]?.get(date)
            val metadata = shift.conflictMetadata
            if (metadata != null) {
                val shown = when (metadata.conflictType) {
                    ConflictType.DOUBLE_BOOKED -> issues.any {
                        (it is RosterIssue.Overlap || (it is RosterIssue.Elsewhere && it.issue == ElsewhereIssue.OVERLAP)) && shift.id in it.shiftIds
                    }
                    ConflictType.REST_VIOLATION -> issues.any { it.group == IssueGroup.REST && shift.id in it.shiftIds }
                    ConflictType.UNAVAILABLE -> status == AvailabilityStatus.UNAVAILABLE
                    else -> false
                }
                if (!shown) {
                    val elsewhere = metadata.conflictingShiftId?.let { it !in ids } ?: true
                    issues += RosterIssue.Forced(uid, shift.id, date, metadata.conflictType, elsewhere)
                }
            }
            if (status == AvailabilityStatus.UNAVAILABLE) issues += RosterIssue.Unavailable(uid, shift.id, date)
            val expired = runCatching { LocalDate.parse(date) }.getOrNull()?.let { expiredChecks(uid, it) }.orEmpty()
            if (expired.isNotEmpty()) issues += RosterIssue.StaffChecksExpired(uid, shift.id, date, expired)
        }

        // The day's lead and supervision.
        val byDate = assigned.groupBy { it.date.take(10) }
        DayStaffing.warnings(assigned, engagements, leadRule).entries.sortedBy { it.key }.forEach { (date, alert) ->
            issues += when (alert.warning) {
                DayStaffWarning.NO_SHIFT_LEAD -> RosterIssue.NoShiftLead(date, byDate[date].orEmpty().map { it.id })
                DayStaffWarning.ONLY_NEEDS_SUPERVISION -> RosterIssue.OnlyNeedsSupervision(date, alert.shiftIds.toList(), alert.windows)
            }
        }
        return RosterIssueReport(issues.sortedWith(compareBy({ it.date ?: "9999" }, { it.group.ordinal })))
    }

    private fun Shift.hours(): Double {
        val start = isoMinutes(plannedTime.start) ?: return 0.0
        val end = isoMinutes(plannedTime.end) ?: return 0.0
        return (end - start).coerceAtLeast(0) / 60.0
    }
}
